/**
 * Chrome specific to the area chart: the crosshair readout.
 *
 * The legend and data table are shared with the other cartesian charts and live
 * in ../cartesianChrome; they are re-exported here so this folder is the one
 * place the area chart reaches for its chrome. The tooltip is its own because a
 * stack changes what a readout owes the reader: the rows have to run in the
 * order the bands are drawn on screen, the total is part of the answer, and a
 * percent chart has to quote the quantity behind the share it is showing.
 */

import type { AreaPoint, ChartSeries } from "./areaUtils";

export { SeriesLegend, SeriesTable } from "../cartesianChrome";

interface AreaTooltipProps {
  readonly active?: boolean;
  readonly label?: string | number;
  readonly payload?: ReadonlyArray<{ readonly dataKey?: string | number; readonly value?: unknown }>;
  /** In draw order: the first entry is the band on the baseline. */
  readonly series: readonly ChartSeries[];
  readonly unit: string;
  /**
   * Whether the bands are stacked, which is what decides row order. Kept
   * separate from `showTotal` even though the chart currently derives both
   * from the same flag: one is about ordering, the other about an extra row.
   */
  readonly stacked: boolean;
  /** Stacked charts add a total row, since the whole is a quantity too. */
  readonly showTotal: boolean;
  /**
   * The values as supplied, passed only by a percent chart, which plots a share
   * and owes the reader the quantity behind it. Looked up by the hovered label
   * rather than by index, so the readout never depends on mouse-tracked state.
   */
  readonly rawPoints?: readonly AreaPoint[];
  readonly valueFormatter: (value: number) => string;
  /** Formats the plotted number, which is a share on a percent chart. */
  readonly plottedFormatter: (value: number) => string;
}

/**
 * Readout for the hovered x.
 *
 * Row order follows the variant. A STACK is painted bottom-up, so the readout
 * is reversed to run top band first -- the order the reader sees them in. A
 * plain area has no bands and no paint order to correct for, so reversing
 * there only makes the tooltip disagree with the legend sitting above it.
 *
 * Text wears ink tokens; identity comes from the coloured swatch beside it.
 */
export function AreaTooltip({
  active,
  label,
  payload,
  series,
  unit,
  stacked,
  showTotal,
  rawPoints,
  valueFormatter,
  plottedFormatter,
}: Readonly<AreaTooltipProps>) {
  if (!active || !payload?.length) return null;

  const rawPoint = rawPoints?.find((point) => String(point?.x) === String(label));

  const ordered = series
    .map((entry) => {
      const match = payload.find((item) => item?.dataKey === entry.key);
      const plotted = typeof match?.value === "number" ? match.value : Number(match?.value);
      if (!Number.isFinite(plotted)) return null;
      const raw = rawPoint?.[entry.key];
      return {
        id: entry.id,
        label: entry.label,
        color: entry.color,
        plotted,
        // Only meaningful when the plotted number is a share of something.
        raw: typeof raw === "number" ? raw : null,
      };
    })
    .filter(
      (
        row,
      ): row is {
        id: string;
        label: string;
        color: string;
        plotted: number;
        raw: number | null;
      } => row != null,
    );

  // Reversed only for a stack, where it undoes the bottom-up paint order.
  // Left alone for a plain area, so the readout matches the legend.
  const rows = stacked ? [...ordered].reverse() : ordered;

  if (!rows.length) return null;

  // The stack's total comes from the quantities, not from the shares, which
  // always sum to 100 and would tell the reader nothing.
  const total = rows.reduce(
    (sum, row) => sum + (row.raw ?? row.plotted),
    0,
  );

  return (
    <div className="max-w-[250px] rounded-xl border border-border bg-card px-3 py-2 shadow-lg">
      <p className="mb-1 break-words text-xs font-bold leading-snug text-foreground">{label}</p>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li key={row.id} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-[3px]"
              style={{ backgroundColor: row.color }}
            />
            <span className="min-w-0 flex-1 break-words text-[11px] font-medium leading-snug text-muted-foreground">
              {row.label}
            </span>
            <span className="shrink-0 text-[11px] font-bold tabular-nums text-foreground">
              {plottedFormatter(row.plotted)}
              {unit}
            </span>
            {/* On a percent chart the share is the plot and the quantity is the
                context, so the reader gets both without a second hover. */}
            {row.raw != null && (
              <span className="shrink-0 text-[10px] font-medium tabular-nums text-muted-foreground">
                {valueFormatter(row.raw)}
              </span>
            )}
          </li>
        ))}
      </ul>

      {showTotal && rows.length > 1 && (
        <div className="mt-1.5 flex items-center gap-2 border-t border-border pt-1.5">
          <span className="min-w-0 flex-1 text-[11px] font-semibold leading-snug text-muted-foreground">
            Total
          </span>
          <span className="shrink-0 text-[11px] font-bold tabular-nums text-foreground">
            {valueFormatter(total)}
          </span>
        </div>
      )}
    </div>
  );
}
